import { MarketInfo } from '@/domain/market-info/marketInfo'
import { repayValidationIssueToMessage, validateRepay } from '@/domain/market-validators/validateRepay'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { MarketWalletInfo } from '@/domain/wallet/useMarketWalletInfo'
import { UseFormReturn } from 'react-hook-form'
import { z } from 'zod'
import {
  AssetInputSchema,
  normalizeDialogFormValues,
  DialogFormNormalizedData as CommonDialogFormNormalizedData,
} from '../../common/logic/form'

import { useDebounce } from '@/utils/useDebounce'
import { useEffect, useRef } from 'react'
import { track } from '@/features/analytics'
import { TokenWithFormValue } from '@/domain/common/types'
import { getRepayMaxValue } from '@/domain/action-max-value-getters/getRepayMaxValue'
import { getCanonicalSymbol } from '@/domain/hooks/useDisplayToken'

export interface AssetFormFields extends TokenWithFormValue {
  maxValue: NormalizedUnitNumber
  maxSelectedFieldName: string
  changeAsset: (newSymbol: TokenSymbol) => void
  customOnChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

export interface FormFieldsForDialog {
  selectedAsset: AssetFormFields
}

export const RepayFormSchema = z.object({
  repayAsset: AssetInputSchema,
})

export type RepayFormSchema = z.input<typeof RepayFormSchema>

export function getRepayDialogFormValidator(marketInfo: MarketInfo, walletInfo: MarketWalletInfo) {
  return RepayFormSchema.superRefine((field, ctx) => {
    if (field.repayAsset.value) {
      const formRepayAsset = normalizeDialogFormValues(field.repayAsset, marketInfo)
      const canonicalRepay = getCanonicalSymbol(marketInfo.chainId, field.repayAsset.symbol)
      const position = marketInfo.findOnePositionBySymbol(canonicalRepay)
      const reserve = position.reserve
      const debt = marketInfo.findOnePositionBySymbol(canonicalRepay).borrowBalance

      const token = marketInfo.findOneTokenBySymbol(canonicalRepay)
      const balance = walletInfo.findWalletBalanceForToken(token)

      const validateIssue = validateRepay({
        value: formRepayAsset.value,
        asset: {
          status: reserve.status,
        },
        user: {
          debt,
          balance,
        },
      })
      if (validateIssue) {
        ctx.addIssue({
          code: 'custom',
          message: repayValidationIssueToMessage[validateIssue],
          path: ['repayAsset', 'value'],
        })
      }
    }
    const { repayAsset } = field
    const repayAmount = NormalizedUnitNumber(repayAsset.value || 0)

    if (repayAmount.isZero()) {
      ctx.addIssue({
        code: 'custom',
        message: 'You must specify an amount to repay.',
        path: ['repayAsset', 'value'],
      })
      return
    }

    const canonicalRepay = getCanonicalSymbol(marketInfo.chainId, repayAsset.symbol)
    const debtToken = marketInfo.findOneTokenBySymbol(canonicalRepay)
    const debt = marketInfo.findOnePositionBySymbol(canonicalRepay).borrowBalance
    const debtInUsd = debt.multipliedBy(debtToken.unitPriceUsd)

    const repayAmountInUsd = repayAmount.multipliedBy(debtToken.unitPriceUsd)
    if (repayAmountInUsd.gt(debtInUsd)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Repay amount cannot be greater than your debt.',
        path: ['repayAsset', 'value'],
      })
    }
  })
}
export interface GetFormFieldsForRepayDialogParams {
  form: UseFormReturn<RepayFormSchema>
  marketInfo: MarketInfo
  walletInfo: MarketWalletInfo
}

export function getFormFieldsForRepayDialog({
  form,
  marketInfo,
  walletInfo,
}: GetFormFieldsForRepayDialogParams): FormFieldsForDialog {
  const { repayAsset } = form.getValues()
  const canonicalRepay = getCanonicalSymbol(marketInfo.chainId, TokenSymbol(repayAsset.symbol))

  const changeAsset = (newSymbol: TokenSymbol): void => {
    form.setValue('repayAsset.symbol', newSymbol)
    form.setValue('repayAsset.isMaxSelected', false)
    if (repayAsset.value) {
      form.trigger('repayAsset.value')
    }
  }

  const customOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    form.setValue('repayAsset.value', value)
    form.setValue('repayAsset.isMaxSelected', false)
    form.trigger()
  }

  const repayMaxValue = getRepayMaxValue({
    asset: {
      status: marketInfo.findOnePositionBySymbol(canonicalRepay).reserve.status,
      isNativeAsset: canonicalRepay === marketInfo.nativeAssetInfo.nativeAssetSymbol,
      debtToken: canonicalRepay,
      collateralToken: undefined,
    },
    user: {
      balance: walletInfo.findWalletBalanceForSymbol(canonicalRepay),
      debt: marketInfo.findOnePositionBySymbol(canonicalRepay).borrowBalance,
      collateralAmount: NormalizedUnitNumber(0),
    },
    chain: {
      minRemainingNativeAsset: marketInfo.nativeAssetInfo.minRemainingNativeAssetBalance,
    },
    marketInfo,
  })

  return {
    selectedAsset: {
      token: marketInfo.findOneTokenBySymbol(canonicalRepay),
      value: repayAsset.value,
      balance: walletInfo.findWalletBalanceForSymbol(canonicalRepay),
      maxValue: repayMaxValue,
      maxSelectedFieldName: 'repayAsset.isMaxSelected',
      changeAsset,
      customOnChange,
    },
  }
}
export interface RepayDialogFormNormalizedData {
  repayAsset: CommonDialogFormNormalizedData
}

function normalizeRepayDialogFormValues(
  formValues: RepayFormSchema,
  marketInfo: MarketInfo,
): RepayDialogFormNormalizedData {
  return {
    repayAsset: normalizeDialogFormValues(formValues.repayAsset, marketInfo),
  }
}

export interface UseDebouncedRepayFormValuesArgs {
  form: UseFormReturn<RepayFormSchema>
  marketInfo: MarketInfo
}
export interface UseDebouncedRepayFormValuesResult {
  debouncedFormValues: RepayDialogFormNormalizedData
  isFormValid: boolean
  isDebouncing: boolean
}

function getRepayDialogFormValuesKey(values: RepayDialogFormNormalizedData): string {
  const repayAssetKey = [
    values.repayAsset.token.address,
    values.repayAsset.value.toFixed(),
    values.repayAsset.isMaxSelected,
  ].join('-')
  return repayAssetKey
}

export function useDebouncedRepayFormValues({
  form,
  marketInfo,
}: UseDebouncedRepayFormValuesArgs): UseDebouncedRepayFormValuesResult {
  const formValues = normalizeRepayDialogFormValues(form.watch(), marketInfo)
  const isFormValid = form.formState.isValid

  const { debouncedValue, isDebouncing } = useDebounce(
    { formValues, isFormValid },
    getRepayDialogFormValuesKey(formValues) + isFormValid.toString(),
  )

  const lastAmountKeyRef = useRef<string>('')

  useEffect(() => {
    const { formValues: debouncedFormValues } = debouncedValue
    const repayAsset = debouncedFormValues.repayAsset
    if (!repayAsset || repayAsset.value.eq(0)) return

    const key = `${repayAsset.token.symbol}_${repayAsset.value.toFixed()}`
    if (key === lastAmountKeyRef.current) return
    lastAmountKeyRef.current = key

    track('lend_borrow_amount_entered', {
      token: repayAsset.token.symbol,
      market: repayAsset.token.address,
      market_type: 'standard',
      action_type: 'repay',
      amount: repayAsset.value.toFixed(),
    })
  }, [debouncedValue])

  return {
    debouncedFormValues: debouncedValue.formValues,
    isFormValid: debouncedValue.isFormValid,
    isDebouncing,
  }
}
